/**
 * MCP Connection Client — pure functions (spec §70 §6, §4.3)
 *
 * No server calls. Config-blobs are SecureBlobs; connection metadata
 * is stored in the user's registry. This module builds the data
 * structures; EngineManager handles persistence.
 */

import type { McpConfig, McpConfigOwner, McpConnectionEntry, McpWorkspaceConfig, McpConnectionPurpose, McpConfigLinks } from './types'
import { generateConfigConnectionToken, generateRandomKey, generateConfigBlobId } from './crypto'

export interface McpConnectionMetadata {
    purpose?: McpConnectionPurpose | string
    parentConfigBlobId?: string
    linkedConfigBlobIds?: string[]
    linkedWorkspaceRefs?: string[]
}

function buildConfigLinks(metadata?: McpConnectionMetadata): McpConfigLinks | undefined {
    if (!metadata) return undefined
    const links: McpConfigLinks = {
        ...(metadata.purpose ? { purpose: metadata.purpose } : {}),
        ...(metadata.parentConfigBlobId ? { parentConfigBlobId: metadata.parentConfigBlobId } : {}),
        ...(metadata.linkedConfigBlobIds?.length ? { linkedConfigBlobIds: metadata.linkedConfigBlobIds } : {}),
        ...(metadata.linkedWorkspaceRefs?.length ? { linkedWorkspaceRefs: metadata.linkedWorkspaceRefs } : {}),
    }
    return Object.keys(links).length > 0 ? links : undefined
}

export function metadataFromConnection(entry: McpConnectionEntry): McpConnectionMetadata {
    return {
        purpose: entry.purpose,
        parentConfigBlobId: entry.parentConfigBlobId,
        linkedConfigBlobIds: entry.linkedConfigBlobIds,
        linkedWorkspaceRefs: entry.linkedWorkspaceRefs,
    }
}

/**
 * Create a new MCP connection.
 *
 * Returns the registry entry + config-connection-token-v2 + config for the SecureBlob.
 * Caller is responsible for:
 *   1. Creating the config SecureBlob in CryptoBlobStore
 *   2. Writing /config.json to it
 *   3. Adding the returned entry to the registry via EngineManager
 */
export function buildNewConnection(
    label: string,
    workspaces: McpWorkspaceConfig[],
    owner: McpConfigOwner,
    mcpReaderSeeds?: Record<string, string>,
    mcpWriterSeeds?: Record<string, string>,
    apiBaseUrl?: string,
    metadata?: McpConnectionMetadata,
): { entry: McpConnectionEntry; token: string; config: McpConfig } {
    const configBlobId = generateConfigBlobId()
    const readKey = generateRandomKey(32)
    const readerSeed = generateRandomKey(32)
    const writerSeed = generateRandomKey(32)
    const links = buildConfigLinks(metadata)

    const config: McpConfig = {
        v: 1,
        type: 'secureblob-mcp-config',
        label,
        owner,
        workspaces,
        ...(links ? { links } : {}),
    }

    const entry: McpConnectionEntry = {
        configBlobId,
        readKey,
        readerSeed,
        writerSeed,
        label,
        ownerKid: owner.primaryKid,
        createdAt: new Date().toISOString(),
        workspaceBlobIds: workspaces.map(w => w.blobId),
        ...(mcpReaderSeeds && Object.keys(mcpReaderSeeds).length > 0
            ? { mcpReaderSeeds }
            : {}),
        ...(mcpWriterSeeds && Object.keys(mcpWriterSeeds).length > 0
            ? { mcpWriterSeeds }
            : {}),
        ...(metadata?.purpose ? { purpose: metadata.purpose } : {}),
        ...(metadata?.parentConfigBlobId ? { parentConfigBlobId: metadata.parentConfigBlobId } : {}),
        ...(metadata?.linkedConfigBlobIds?.length ? { linkedConfigBlobIds: metadata.linkedConfigBlobIds } : {}),
        ...(metadata?.linkedWorkspaceRefs?.length ? { linkedWorkspaceRefs: metadata.linkedWorkspaceRefs } : {}),
    }

    const token = generateConfigConnectionToken(entry, apiBaseUrl ?? 'https://secureblob.hoglundit.se/api/cryptoblobstore')

    return { entry, token, config }
}

/**
 * Build an updated config-blob payload from existing connection + new workspace list.
 */
export function buildUpdatedConfig(
    label: string,
    workspaces: McpWorkspaceConfig[],
    owner: McpConfigOwner,
    metadata?: McpConnectionMetadata,
): McpConfig {
    const links = buildConfigLinks(metadata)
    return {
        v: 1,
        type: 'secureblob-mcp-config',
        label,
        owner,
        workspaces,
        ...(links ? { links } : {}),
    }
}

// Re-export token helper for convenience
export { generateConfigConnectionToken }
